/**
 * Go anywhere by typing. Cmd-K or Ctrl-K opens it, and so does "/" when the reader is
 * not already in a field, because a browser may claim Cmd-K for its own address bar and
 * then the page never sees the key at all. Listening in the capture phase is what makes
 * the browsers that do let it through reach us first.
 */
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { navigate } from "./link.js";

export interface Command { id: string; what: string; where: string; href: string; keywords?: string }

/** The bar's Search button, and anything else that wants to open it without owning its state. */
const OPEN = "trellis:open-palette";
export function openPalette(): void { dispatchEvent(new Event(OPEN)); }

export function Palette({ commands }: { commands: Command[] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [at, setAt] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setOpen((v) => !v); return; }
      if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); setOpen(true); return; }
      if (e.key === "Escape") setOpen(false);
    };
    const onAsk = () => setOpen(true);
    addEventListener("keydown", onKey, true);
    addEventListener(OPEN, onAsk);
    return () => { removeEventListener("keydown", onKey, true); removeEventListener(OPEN, onAsk); };
  }, []);

  useEffect(() => {
    if (!open) return;
    setQ(""); setAt(0);
    const returnTo = document.activeElement as HTMLElement | null;
    input.current?.focus();
    // The page behind stays where it was, and Tab does not walk off into it.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; returnTo?.focus?.(); };
  }, [open]);

  const hits = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return commands.slice(0, 8);
    return commands
      .filter((c) => `${c.what} ${c.where} ${c.keywords ?? ""}`.toLowerCase().includes(needle))
      .slice(0, 8);
  }, [q, commands]);

  if (!open) return null;
  const go = (c: Command | undefined) => { if (!c) return; setOpen(false); navigate(c.href); };

  return (
    <div class="palette-scrim" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div class="palette" role="dialog" aria-modal="true" aria-label="Go to">
        <input
          ref={input} type="text" class="palette-q" placeholder="Go to a person, a program, a unit…"
          // A placeholder is not a name: it goes the moment anyone types. The active
          // option is named too, so a screen reader follows the arrow keys.
          aria-label="Go to" value={q} role="combobox" aria-expanded="true" aria-controls="palette-list"
          aria-autocomplete="list" {...(hits[at] ? { "aria-activedescendant": `palette-opt-${hits[at].id}` } : {})}
          onInput={(e) => { setQ((e.currentTarget as HTMLInputElement).value); setAt(0); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setAt((i) => Math.min(i + 1, hits.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setAt((i) => Math.max(i - 1, 0)); }
            else if (e.key === "Enter") { e.preventDefault(); go(hits[at]); }
            // The only control in the dialog, so Tab keeps the reader inside it.
            else if (e.key === "Tab") e.preventDefault();
          }}
        />
        <ul class="palette-list" id="palette-list" role="listbox">
          {hits.length === 0
            ? <li class="palette-none">Nothing matches that.</li>
            : hits.map((c, i) => (
              <li key={c.id} id={`palette-opt-${c.id}`} role="option" aria-selected={i === at} class={i === at ? "on" : ""}
                  onMouseEnter={() => setAt(i)} onClick={() => go(c)}>
                <span class="what">{c.what}</span>
                <span class="where">{c.where}</span>
              </li>
            ))}
        </ul>
        <p class="palette-foot">Up and down to move, Enter to go, Escape to close. Press <kbd>/</kbd> anywhere to open this.</p>
      </div>
    </div>
  );
}
